import { SimplePackagingSpecification } from 'api';
import { formatPackedOn, formatStorageType, formatStrapping } from 'form';
import { Button, SelectableCard, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from 'ui';
import { DeleteOutlined, EditOutlined } from 'ui/icons';

type Props = {
  packagingSpecifications: SimplePackagingSpecification;
  isSelected: boolean;
  onSelect: (specId: number) => void;
  onClickEdit: (specId: number) => void;
  onClickDelete: (specId: number) => void;
};

type SpecificationRowProps = {
  name: string;
  unit?: string | null;
  minValue?: string | null;
  maxValue?: string | null;
  text?: string | null;
};

const SpecificationRow = ({ maxValue, minValue, name, unit, text }: SpecificationRowProps) => {
  let valueLabel = '';

  if (minValue && maxValue) {
    valueLabel = `${minValue} - ${maxValue}`;
  } else if (minValue || maxValue) {
    valueLabel = minValue || maxValue || '';
  } else {
    valueLabel = text || '';
  }
  if (!valueLabel) {
    return null;
  }

  const unitLabel = unit ? `(${unit})` : '';
  return (
    <TableRow>
      <TableCell align="right" sx={{ paddingRight: 1 }} width="45%">
        <Typography>{name}:</Typography>
      </TableCell>
      <TableCell align="left" sx={{ paddingLeft: 0 }}>
        <Typography fontWeight="bold">
          {valueLabel} {unitLabel}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export const PackagingSpecificationsCard = ({
  packagingSpecifications,
  isSelected,
  onSelect,
  onClickEdit,
  onClickDelete,
}: Props) => {
  const title = packagingSpecifications.title || 'Unnamed packaging specification';

  const specifications = [
    {
      name: 'Storage type',
      text: formatStorageType(packagingSpecifications.storage_type),
    },
    {
      name: 'Strapping',
      text: packagingSpecifications.strapping.map(formatStrapping).join(', '),
    },
    {
      name: 'Packed on',
      text: packagingSpecifications.packed_on ? formatPackedOn(packagingSpecifications.packed_on) : '',
    },
    {
      maxValue: packagingSpecifications.max_weight,
      minValue: packagingSpecifications.min_weight,
      name: 'Unit weight',
      unit: 'kg',
    },
    {
      maxValue: packagingSpecifications.max_load_weight,
      minValue: packagingSpecifications.min_load_weight,
      name: 'Load weight',
      unit: 'MT',
    },
    {
      maxValue: packagingSpecifications.max_height,
      minValue: packagingSpecifications.min_height,
      name: 'Height',
      unit: packagingSpecifications.dimension_unit,
    },
    {
      maxValue: packagingSpecifications.max_width,
      minValue: packagingSpecifications.min_width,
      name: 'Width',
      unit: packagingSpecifications.dimension_unit,
    },
    {
      maxValue: packagingSpecifications.max_length,
      minValue: packagingSpecifications.min_length,
      name: 'Length',
      unit: packagingSpecifications.dimension_unit,
    },
    {
      maxValue: packagingSpecifications.max_density,
      minValue: packagingSpecifications.min_density,
      name: 'Density',
      unit: packagingSpecifications.density_unit,
    },
  ];

  return (
    <SelectableCard
      title={title}
      isSelected={isSelected}
      onClick={() => onSelect(packagingSpecifications.id)}
      data-testid={`packaging-specifications-card-${packagingSpecifications.id}`}
    >
      <TableContainer>
        <Table>
          <TableBody>
            {specifications.map((specification, index) => (
              <SpecificationRow key={index} {...specification} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="center" spacing={2} padding={2}>
        <Button
          variant="secondary"
          onClick={() => onClickDelete(packagingSpecifications.id)}
          data-testid={`delete-packaging-specification-${packagingSpecifications.id}`}
        >
          <DeleteOutlined />
        </Button>
        <Button
          variant="secondary"
          onClick={() => onClickEdit(packagingSpecifications.id)}
          data-testid={`edit-packaging-specification-${packagingSpecifications.id}`}
        >
          <EditOutlined />
        </Button>
      </Stack>
    </SelectableCard>
  );
};
